import React, { useState } from 'react';
import {
  Add,
  AttachFile,
  Build,
  CheckCircle,
  Description,
  Edit,
  ElectricalServices,
  Home,
  Kitchen,
  PhotoCamera,
  Plumbing,
  Roofing,
  Schedule,
  Warning,
  WaterDrop,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  CardContent,
  CardHeader,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { format } from 'date-fns';

import GlassButton from '@/components/ui/GlassButton';
import GlassCard from '@/components/ui/GlassCard';

interface InspectionCategory {
  id: string;
  name: string;
  icon: React.ReactNode;
  status: 'pending' | 'inspected' | 'issues_found' | 'resolved';
  notes: string;
  findings: string[];
  photos: string[];
  actionItems: string[];
  inspectorName?: string;
  inspectionDate?: string;
  nextInspectionDate?: string;
}

interface InspectionPropertyProps {
  contractId: string;
  userRole: 'buyer' | 'seller' | 'agent';
  userName?: string;
}

const InspectionProperty: React.FC<InspectionPropertyProps> = ({
  contractId,
  userRole,
  userName,
}) => {
  const theme = useTheme();
  const [selectedCategory, setSelectedCategory] =
    useState<InspectionCategory | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [notes, setNotes] = useState('');
  const [findings, setFindings] = useState('');
  const [actionItems, setActionItems] = useState('');

  // Mock inspection categories - in real implementation, this would come from contract data
  const [inspectionCategories, setInspectionCategories] = useState<
    InspectionCategory[]
  >([
    {
      id: 'hvac',
      name: 'HVAC System',
      icon: <Build />,
      status: 'pending',
      notes: '',
      findings: [],
      photos: [],
      actionItems: [],
    },
    {
      id: 'appliances',
      name: 'Appliances',
      icon: <Kitchen />,
      status: 'pending',
      notes: '',
      findings: [],
      photos: [],
      actionItems: [],
    },
    {
      id: 'plumbing',
      name: 'Plumbing',
      icon: <Plumbing />,
      status: 'pending',
      notes: '',
      findings: [],
      photos: [],
      actionItems: [],
    },
    {
      id: 'structural',
      name: 'Structural',
      icon: <Home />,
      status: 'pending',
      notes: '',
      findings: [],
      photos: [],
      actionItems: [],
    },
    {
      id: 'water',
      name: 'Water/Septic',
      icon: <WaterDrop />,
      status: 'pending',
      notes: '',
      findings: [],
      photos: [],
      actionItems: [],
    },
    {
      id: 'electrical',
      name: 'Electrical',
      icon: <ElectricalServices />,
      status: 'pending',
      notes: '',
      findings: [],
      photos: [],
      actionItems: [],
    },
    {
      id: 'roofing',
      name: 'Roofing',
      icon: <Roofing />,
      status: 'pending',
      notes: '',
      findings: [],
      photos: [],
      actionItems: [],
    },
    {
      id: 'permits',
      name: 'Permits',
      icon: <Description />,
      status: 'pending',
      notes: '',
      findings: [],
      photos: [],
      actionItems: [],
    },
  ]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'inspected':
        return 'success';
      case 'issues_found':
        return 'error';
      case 'resolved':
        return 'info';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'inspected':
        return <CheckCircle />;
      case 'issues_found':
        return <Warning />;
      case 'resolved':
        return <CheckCircle />;
      default:
        return <Schedule />;
    }
  };

  const handleEditCategory = (category: InspectionCategory) => {
    setSelectedCategory(category);
    setNotes(category.notes);
    setFindings(category.findings.join('\n'));
    setActionItems(category.actionItems.join('\n'));
    setEditDialogOpen(true);
  };

  const handleSaveCategory = () => {
    if (!selectedCategory) return;

    const updatedCategories = inspectionCategories.map((cat) =>
      cat.id === selectedCategory.id
        ? {
            ...cat,
            notes,
            findings: findings.split('\n').filter((f) => f.trim()),
            actionItems: actionItems.split('\n').filter((a) => a.trim()),
            status: findings.trim() ? 'issues_found' : 'inspected',
            inspectorName: userName,
            inspectionDate: new Date().toISOString(),
          }
        : cat
    );

    setInspectionCategories(updatedCategories);
    setEditDialogOpen(false);
    setSelectedCategory(null);
  };

  const handleStatusChange = (
    categoryId: string,
    newStatus: InspectionCategory['status']
  ) => {
    const updatedCategories = inspectionCategories.map((cat) =>
      cat.id === categoryId ? { ...cat, status: newStatus } : cat
    );
    setInspectionCategories(updatedCategories);
  };

  const completedCount = inspectionCategories.filter(
    (cat) => cat.status === 'inspected' || cat.status === 'resolved'
  ).length;
  const issuesCount = inspectionCategories.filter(
    (cat) => cat.status === 'issues_found'
  ).length;
  const totalCount = inspectionCategories.length;

  const panelBg = alpha(theme.palette.primary.main, 0.05);
  const panelBorder = alpha(theme.palette.primary.main, 0.14);
  const categoryCardContentBg = alpha(theme.palette.primary.main, 0.08);
  const categoryCardContentHoverBg = alpha(theme.palette.primary.main, 0.15);
  const headingColor = theme.palette.text.primary;
  const bodyColor = theme.palette.text.secondary;

  const getStatusChipSx = (status: InspectionCategory['status']) => {
    if (status === 'pending') {
      return {
        bgcolor: alpha(theme.palette.warning.main, 0.12),
        color: headingColor,
        '& .MuiChip-icon': {
          color: theme.palette.warning.main,
        },
      };
    }
    return {
      '& .MuiChip-icon': {
        color: 'inherit',
      },
    };
  };

  return (
    <Box
      sx={{
        p: 3,
        borderRadius: 3,
        bgcolor: panelBg,
        border: `1px solid ${panelBorder}`,
      }}
    >
      {/* Header */}
      <GlassCard variant="medium" sx={{ mb: 3 }}>
        <CardHeader
          title={
            <Typography variant="h5" sx={{ color: headingColor }}>
              Inspection & Property Assessment
            </Typography>
          }
          subheader={
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2" sx={{ color: bodyColor }}>
                Comprehensive property inspection tracking for all major systems
                and components.
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                <Chip
                  label={`${completedCount}/${totalCount} Completed`}
                  color="success"
                  icon={<CheckCircle />}
                />
                <Chip
                  label={`${issuesCount} Issues Found`}
                  color="error"
                  icon={<Warning />}
                />
              </Box>
            </Box>
          }
        />
      </GlassCard>

      {/* Inspection Categories Grid */}
      <Grid container spacing={3}>
        {inspectionCategories.map((category) => (
          <Grid item xs={12} sm={6} md={4} key={category.id}>
            <GlassCard
              variant="light"
              hoverable={false}
              sx={{
                height: '100%',
                transition: 'transform 0.22s ease, box-shadow 0.22s ease',
                '& > .MuiCardContent-root': {
                  bgcolor: categoryCardContentBg,
                  borderRadius: 2,
                  transition: 'background-color 0.22s ease',
                },
                '@media (hover: hover)': {
                  '&:hover': {
                    transform: 'translateY(-3px)',
                    boxShadow: `0 12px 32px ${alpha(
                      theme.palette.common.black,
                      0.14
                    )}`,
                  },
                  '&:hover > .MuiCardContent-root': {
                    bgcolor: categoryCardContentHoverBg,
                  },
                },
              }}
            >
              <CardContent>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 2,
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      color: headingColor,
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        color: theme.palette.primary.main,
                      }}
                    >
                      {category.icon}
                    </Box>
                    <Typography variant="h6" sx={{ color: headingColor }}>
                      {category.name}
                    </Typography>
                  </Box>
                  <Chip
                    label={category.status.replace('_', ' ')}
                    color={getStatusColor(category.status)}
                    icon={getStatusIcon(category.status)}
                    size="small"
                    sx={{
                      textTransform: 'capitalize',
                      ...getStatusChipSx(category.status),
                    }}
                  />
                </Box>

                {category.notes && (
                  <Typography variant="body2" sx={{ color: bodyColor, mb: 2 }}>
                    {category.notes}
                  </Typography>
                )}

                {category.findings.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{ color: headingColor, mb: 1 }}
                    >
                      Findings:
                    </Typography>
                    <List dense>
                      {category.findings.map((finding, index) => (
                        <ListItem key={index} sx={{ py: 0 }}>
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            <Warning fontSize="small" color="error" />
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Typography
                                variant="body2"
                                sx={{ color: bodyColor }}
                              >
                                {finding}
                              </Typography>
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                )}

                {category.actionItems.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{ color: headingColor, mb: 1 }}
                    >
                      Action Items:
                    </Typography>
                    <List dense>
                      {category.actionItems.map((item, index) => (
                        <ListItem key={index} sx={{ py: 0 }}>
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            <Schedule fontSize="small" color="warning" />
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Typography
                                variant="body2"
                                sx={{ color: bodyColor }}
                              >
                                {item}
                              </Typography>
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                )}

                <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                  <GlassButton
                    size="small"
                    startIcon={<Edit />}
                    onClick={() => handleEditCategory(category)}
                  >
                    Edit
                  </GlassButton>
                  <GlassButton
                    size="small"
                    startIcon={<PhotoCamera />}
                    variant="outlined"
                  >
                    Photos
                  </GlassButton>
                </Box>

                {category.inspectorName && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: theme.palette.greyShades.grey3,
                      mt: 1,
                      display: 'block',
                    }}
                  >
                    Inspected by: {category.inspectorName} on{' '}
                    {category.inspectionDate
                      ? format(
                          new Date(category.inspectionDate),
                          'MMM dd, yyyy'
                        )
                      : 'N/A'}
                  </Typography>
                )}
              </CardContent>
            </GlassCard>
          </Grid>
        ))}
      </Grid>

      {/* Edit Category Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            background: theme.palette.mainColors.background1,
            color: theme.palette.mainColors.white,
          }}
        >
          Edit {selectedCategory?.name} Inspection
        </DialogTitle>
        <DialogContent
          sx={{
            background: theme.palette.mainColors.background,
            color: theme.palette.greyShades.grey1,
          }}
        >
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Inspection Notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="General observations and notes..."
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Issues Found (one per line)"
                value={findings}
                onChange={(e) => setFindings(e.target.value)}
                placeholder="List any issues or concerns found during inspection..."
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Action Items (one per line)"
                value={actionItems}
                onChange={(e) => setActionItems(e.target.value)}
                placeholder="List required actions or repairs..."
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions
          sx={{ background: theme.palette.mainColors.background1 }}
        >
          <GlassButton onClick={() => setEditDialogOpen(false)}>
            Cancel
          </GlassButton>
          <GlassButton onClick={handleSaveCategory} variant="contained">
            Save Inspection
          </GlassButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InspectionProperty;
